import { gql, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Paper,
  Typography,
  Container,
  Grid,
  Alert,
  AlertTitle,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Skeleton,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Stack,
  Fab,
  Collapse,
  Badge,
} from "@mui/material";
import {
  Lan,
  ArrowBackIosNew,
  ArrowForwardIos,
  Refresh,
  GridView,
  ViewList,
  Settings,
  Analytics,
  FilterList,
  Search,
  Sort,
  Download,
  Share,
  Print,
  Visibility,
  Warning,
  CheckCircle,
  Error,
  TrendingUp,
  TrendingDown,
  Assessment,
  Restaurant,
  Inventory2,
  Storage,
  Kitchen,
  AutoAwesome,
  KeyboardArrowUp,
  ExpandMore,
  ExpandLess,
  Notifications,
  Info,
  Fastfood,
  Grain,
  Liquor,
  LocalBar,
  EmojiFoodBeverage,
  LocalDining,
} from "@mui/icons-material";
import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Preloader from "../Components/Preloader/Preloader";
import InventoryBox from "../Components/inventory/InventoryBox";
import { useKitchenDetail } from "../Context/GetMyKitchenDetailContext";
import { useGetMe } from "../Context/GetMeContext";
import CustomModal from "../Components/CustomModal";
import InventoryEditBox from "../Components/inventory/InventoryEditBox";
import { useInventory } from "../Context/MyInventoryContext";
import BreadCrumb from "../Components/BreadCrumb";

const GET_BOXES = gql`
  query GetBoxes(
    $kitchen_id: ID!
    $user_id: ID!
    $inventory_type: InventoryType!
  ) {
    getBoxes(
      kitchen_id: $kitchen_id
      user_id: $user_id
      inventory_type: $inventory_type
    ) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
      created
      modified
    }
  }
`;

// Enhanced pantry configurations
const pantryConfig = {
  GRAIN: {
    icon: Grain,
    color: "#8bc34a",
    gradient: "linear-gradient(135deg, #8bc34a, #689f38)",
    title: "Grains",
    description: "Essential grains and cereals",
    bgPattern: "🌾",
    unit: "g",
    lowThreshold: 30, // 30% of capacity
    criticalThreshold: 15, // 15% of capacity
  },
  SPICE: {
    icon: Fastfood,
    color: "#ff9800",
    gradient: "linear-gradient(135deg, #ff9800, #f57c00)",
    title: "Spices",
    description: "Aromatic spices and seasonings",
    bgPattern: "🌶️",
    unit: "g",
    lowThreshold: 25,
    criticalThreshold: 10,
  },
  OIL: {
    icon: Liquor,
    color: "#ffc107",
    gradient: "linear-gradient(135deg, #ffc107, #ff8f00)",
    title: "Glass Bottles",
    description: "Cooking oils and liquid ingredients",
    bgPattern: "🛢️",
    unit: "ml",
    lowThreshold: 25,
    criticalThreshold: 10,
  },
  PASTE: {
    icon: LocalBar,
    color: "#e91e63",
    gradient: "linear-gradient(135deg, #e91e63, #c2185b)",
    title: "Sauce Bottles",
    description: "Sauces, pastes and condiments",
    bgPattern: "🍯",
    unit: "ml",
    lowThreshold: 20,
    criticalThreshold: 10,
  },
  MANUALBOWL: {
    icon: EmojiFoodBeverage,
    color: "#9c27b0",
    gradient: "linear-gradient(135deg, #9c27b0, #7b1fa2)",
    category: "Utensils",
    title: "Bowls",
    description: "Serving bowls and containers",
    bgPattern: "🥣",
    unit: "g",
    thresholds: {
      good: 15,
      low: 8,
      critical: 4,
    },
  },
  MANUALCUP: {
    icon: LocalDining,
    color: "#3f51b5",
    gradient: "linear-gradient(135deg, #3f51b5, #303f9f)",
    category: "Utensils",
    title: "Cups",
    description: "Cups and drinking vessels",
    bgPattern: "☕",
    unit: "g",
    thresholds: {
      good: 20,
      low: 10,
      critical: 5,
    },
  },
};

// Box status helper function
const getBoxStatus = (boxFilled, boxCapacity, pantryType) => {
  if (!boxCapacity || boxCapacity === 0) {
    return {
      status: "UNKNOWN",
      color: "#9e9e9e",
      label: "Unknown",
      icon: Info,
      severity: "info",
      percentage: 0,
    };
  }

  const percentage = (boxFilled / boxCapacity) * 100;
  const config = pantryConfig[pantryType];

  if (percentage === 0) {
    return {
      status: "EMPTY",
      color: "#f44336",
      label: "Empty",
      icon: Error,
      severity: "error",
      percentage,
    };
  } else if (percentage <= config.criticalThreshold) {
    return {
      status: "CRITICAL",
      color: "#ff5722",
      label: "Critical Low",
      icon: Warning,
      severity: "error",
      percentage,
    };
  } else if (percentage <= config.lowThreshold) {
    return {
      status: "LOW",
      color: "#ff9800",
      label: "Running Low",
      icon: TrendingDown,
      severity: "warning",
      percentage,
    };
  } else {
    return {
      status: "GOOD",
      color: "#4caf50",
      label: "Well Stocked",
      icon: CheckCircle,
      severity: "success",
      percentage,
    };
  }
};

// Loading skeleton components
const BoxSkeleton = () => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        height: "100%",
        minHeight: 280,
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[900]}90 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[50]}90 100%)`,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" width="100%" height={8} sx={{ mb: 1 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Skeleton variant="text" width="30%" height={14} />
          <Skeleton variant="text" width="20%" height={14} />
        </Box>
        <Skeleton variant="rounded" width="100%" height={40} />
      </CardContent>
    </Card>
  );
};

const StatCardSkeleton = () => {
  const theme = useTheme();

  return (
    <Paper
      elevation={4}
      sx={{
        p: 3,
        borderRadius: 4,
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Skeleton variant="circular" width={56} height={56} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="text" width="40%" height={18} />
        </Box>
      </Box>
    </Paper>
  );
};

// Enhanced Statistics Card Component
const StatisticsCard = ({
  icon: IconComponent,
  label,
  value,
  color,
  gradient,
  suffix = "",
}) => {
  const theme = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 3,
          borderRadius: 4,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
              : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 12px 40px rgba(0,0,0,0.3)`
                : `0 12px 40px rgba(0,0,0,0.12)`,
            transform: "translateY(-2px)",
          },
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: `${color}10`,
            opacity: 0.3,
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                background: gradient,
                boxShadow: `0 4px 20px ${color}30`,
              }}
            >
              <IconComponent sx={{ fontSize: 28, color: "white" }} />
            </Avatar>
          </motion.div>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: "2rem",
                color: theme.palette.text.primary,
                lineHeight: 1,
                mb: 0.5,
              }}
            >
              {value}
              {suffix}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {label}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
};

const PantryBoxes = () => {
  const { boxType } = useParams();
  const { user } = useGetMe();
  const userdata = user?.me;
  const { kitchenDetail } = useKitchenDetail();
  const { inventory } = useInventory();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [pantryCurrentIdx, setPantryCurrentIdx] = useState(0);
  const [currentPantry, setCurrentPantry] = useState(boxType);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedBox, setSelectedBox] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showStatistics, setShowStatistics] = useState(true);
  const [alertsExpanded, setAlertsExpanded] = useState(true);

  useEffect(() => {
    const getPantryCurrentIdx = () => {
      if (!inventory?.length) return;
      const index = inventory.findIndex(
        (item) => item.inventoryType === boxType
      );
      if (index !== -1) {
        setPantryCurrentIdx(index);
      } else {
        console.warn("Pantry type not found!");
      }
    };
    getPantryCurrentIdx();
  }, [boxType, inventory]);

  const handlePrevPantry = () => {
    let selectedPantry = {};
    if (pantryCurrentIdx > 0) {
      const newIndex = pantryCurrentIdx - 1;
      setPantryCurrentIdx(newIndex);
      selectedPantry = inventory[newIndex];
    } else if (pantryCurrentIdx === 0) {
      const lastIndex = inventory.length - 1;
      setPantryCurrentIdx(lastIndex);
      selectedPantry = inventory[lastIndex];
    }
    setCurrentPantry(selectedPantry?.inventoryType);
  };

  const handleNextPantry = () => {
    let selectedPantry = {};
    if (pantryCurrentIdx < inventory.length - 1) {
      const newIndex = pantryCurrentIdx + 1;
      setPantryCurrentIdx(newIndex);
      selectedPantry = inventory[newIndex];
    } else if (pantryCurrentIdx === inventory.length - 1) {
      const firstIndex = 0;
      setPantryCurrentIdx(firstIndex);
      selectedPantry = inventory[firstIndex];
    }
    setCurrentPantry(selectedPantry?.inventoryType);
  };

  const nextPantryType =
    Array.isArray(inventory) &&
    inventory?.length > 0 &&
    pantryCurrentIdx + 1 < inventory?.length
      ? inventory[pantryCurrentIdx + 1]?.inventoryType
      : inventory?.[0]?.inventoryType;

  const previousPantryType =
    Array.isArray(inventory) &&
    inventory.length > 0 &&
    pantryCurrentIdx - 1 >= 0
      ? inventory[pantryCurrentIdx - 1]?.inventoryType
      : inventory?.[inventory.length - 1]?.inventoryType;

  const { loading, data, error, refetch } = useQuery(GET_BOXES, {
    variables: {
      kitchen_id: kitchenDetail?.kitchenId,
      user_id: userdata?.id,
      inventory_type: currentPantry,
    },
    skip: !kitchenDetail?.kitchenId || !userdata?.id,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!data?.getBoxes?.length) return null;

    const boxes = data.getBoxes;
    const config = pantryConfig[currentPantry];

    const totalBoxes = boxes.length;
    const filledBoxes = boxes.filter((box) => box.box_filled > 0).length;
    const emptyBoxes = boxes.filter((box) => box.box_filled === 0).length;
    const averageCapacity =
      boxes.reduce((sum, box) => sum + (box.box_capacity || 0), 0) / totalBoxes;
    const totalFilled = boxes.reduce(
      (sum, box) => sum + (box.box_filled || 0),
      0
    );
    const totalCapacity = boxes.reduce(
      (sum, box) => sum + (box.box_capacity || 0),
      0
    );
    const utilizationPercentage =
      totalCapacity > 0 ? (totalFilled / totalCapacity) * 100 : 0;

    return {
      totalBoxes,
      filledBoxes,
      emptyBoxes,
      averageCapacity,
      totalFilled,
      totalCapacity,
      utilizationPercentage,
      unit: config?.unit || "units",
    };
  }, [data?.getBoxes, currentPantry]);

  // Get alerts for low stock boxes
  const alerts = useMemo(() => {
    if (!data?.getBoxes?.length) return [];

    return data.getBoxes
      .map((box) => {
        const status = getBoxStatus(
          box.box_filled,
          box.box_capacity,
          currentPantry
        );
        return { ...box, status };
      })
      .filter(
        (box) =>
          box.status.severity === "error" || box.status.severity === "warning"
      )
      .sort((a, b) => {
        if (a.status.severity === "error" && b.status.severity === "warning")
          return -1;
        if (a.status.severity === "warning" && b.status.severity === "error")
          return 1;
        return 0;
      });
  }, [data?.getBoxes, currentPantry]);

  const handleIntiateUpdate = (boxId) => {
    setShowUpdateModal((prev) => !prev);
    setSelectedBox(boxId || null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetch();
    } catch (error) {
      console.error("Failed to refresh boxes:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Scroll management
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const config = pantryConfig[currentPantry] || pantryConfig.GRAIN;
  const IconComponent = config.icon;

  if (loading && !data) {
    return <Preloader />;
  }

  return (
    <>
      <BreadCrumb
        links={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Pantry", href: "/inventory" },
        ]}
        current={config.title}
      />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Enhanced Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={12}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 4,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, 
                  ${config.color}15 0%, 
                  ${theme.palette.background.paper} 20%,
                  ${theme.palette.background.paper} 80%,
                  ${config.color}15 100%)`
                  : `linear-gradient(135deg, 
                  ${config.color}15 0%, 
                  ${theme.palette.background.paper} 20%,
                  ${theme.palette.background.paper} 80%,
                  ${config.color}15 100%)`,
              border: `1px solid ${theme.palette.divider}`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.05,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${config.color.slice(
                  1
                )}' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                position: "relative",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <motion.div
                  animate={{
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    repeatDelay: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      background: config.gradient,
                      boxShadow: `0 8px 32px ${config.color}40`,
                    }}
                  >
                    <IconComponent sx={{ fontSize: 40, color: "white" }} />
                  </Avatar>
                </motion.div>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.8rem" },
                      background: config.gradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    {config.title} Pantry
                  </Typography>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.2rem" },
                      fontWeight: 500,
                    }}
                  >
                    {config.description}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Tooltip title="Refresh Data">
                    <IconButton
                      onClick={handleRefresh}
                      disabled={refreshing}
                      sx={{
                        background: config.gradient,
                        color: "white",
                        width: 56,
                        height: 56,
                        boxShadow: `0 4px 16px ${config.color}40`,
                        "&:hover": {
                          background: config.gradient,
                          boxShadow: `0 12px 40px ${config.color}60`,
                        },
                      }}
                    >
                      <motion.div
                        animate={refreshing ? { rotate: 360 } : {}}
                        transition={{
                          duration: 1,
                          repeat: refreshing ? Infinity : 0,
                        }}
                      >
                        <Refresh sx={{ fontSize: 28 }} />
                      </motion.div>
                    </IconButton>
                  </Tooltip>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Tooltip
                    title={`Switch to ${
                      viewMode === "grid" ? "List" : "Grid"
                    } View`}
                  >
                    <IconButton
                      onClick={() =>
                        setViewMode(viewMode === "grid" ? "list" : "grid")
                      }
                      sx={{
                        background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                        color: "white",
                        width: 56,
                        height: 56,
                        boxShadow: `0 4px 16px ${theme.palette.secondary.main}40`,
                        "&:hover": {
                          background: `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
                          boxShadow: `0 12px 40px ${theme.palette.secondary.main}60`,
                        },
                      }}
                    >
                      {viewMode === "grid" ? (
                        <ViewList sx={{ fontSize: 28 }} />
                      ) : (
                        <GridView sx={{ fontSize: 28 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </motion.div>
              </Box>
            </Box>
          </Paper>
        </motion.div>

        {/* Navigation Section */}
        {inventory?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Paper
              elevation={6}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 4,
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[900]}50 100%)`
                    : `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[50]}50 100%)`,
                border: `1px solid ${theme.palette.divider}30`,
                backdropFilter: "blur(20px)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    onClick={handlePrevPantry}
                    variant="outlined"
                    size="large"
                    startIcon={<ArrowBackIosNew />}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      background:
                        theme.palette.mode === "dark"
                          ? `linear-gradient(135deg, ${theme.palette.background.paper}90 0%, ${theme.palette.grey[800]}50 100%)`
                          : `linear-gradient(135deg, ${theme.palette.background.paper}90 0%, ${theme.palette.grey[100]}50 100%)`,
                      border: `2px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        border: `2px solid ${config.color}`,
                        background: `${config.color}10`,
                        transform: "translateX(-2px)",
                      },
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {pantryConfig[previousPantryType]?.title ||
                        previousPantryType}
                    </Typography>
                  </Button>
                </motion.div>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Lan sx={{ color: config.color }} />
                  {config.title}
                </Typography>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    onClick={handleNextPantry}
                    variant="outlined"
                    size="large"
                    endIcon={<ArrowForwardIos />}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      background:
                        theme.palette.mode === "dark"
                          ? `linear-gradient(135deg, ${theme.palette.background.paper}90 0%, ${theme.palette.grey[800]}50 100%)`
                          : `linear-gradient(135deg, ${theme.palette.background.paper}90 0%, ${theme.palette.grey[100]}50 100%)`,
                      border: `2px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        border: `2px solid ${config.color}`,
                        background: `${config.color}10`,
                        transform: "translateX(2px)",
                      },
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {pantryConfig[nextPantryType]?.title || nextPantryType}
                    </Typography>
                  </Button>
                </motion.div>
              </Box>
            </Paper>
          </motion.div>
        )}

        {/* Error Handling */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Alert
              severity="error"
              icon={<Error />}
              sx={{
                mb: 4,
                borderRadius: 4,
                "& .MuiAlert-icon": {
                  fontSize: "2rem",
                },
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                Failed to Load Pantry Data
              </AlertTitle>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {error.message ||
                  "An unexpected error occurred while fetching pantry data."}
              </Typography>
              <Button
                variant="contained"
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  "&:hover": {
                    background: `linear-gradient(45deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`,
                  },
                }}
              >
                {refreshing ? "Retrying..." : "Retry"}
              </Button>
            </Alert>
          </motion.div>
        )}

        {/* Statistics Section */}
        {statistics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1], rotate: [0, 180, 360] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                >
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      background: config.gradient,
                      boxShadow: `0 4px 20px ${config.color}30`,
                    }}
                  >
                    <Assessment sx={{ color: "white", fontSize: 28 }} />
                  </Avatar>
                </motion.div>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{
                      background: config.gradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 0.5,
                    }}
                  >
                    {config.title} Statistics
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ opacity: 0.8 }}
                  >
                    Real-time box-level pantry analytics
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setShowStatistics(!showStatistics)}
                  sx={{ ml: "auto" }}
                >
                  {showStatistics ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={showStatistics}>
                {loading ? (
                  <Grid container spacing={3}>
                    {Array.from({ length: 4 }).map((_, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <StatCardSkeleton />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatisticsCard
                        icon={Storage}
                        label="Total Boxes"
                        value={statistics.totalBoxes}
                        color="#2196f3"
                        gradient="linear-gradient(135deg, #2196f3, #1976d2)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatisticsCard
                        icon={Inventory2}
                        label="Filled Boxes"
                        value={statistics.filledBoxes}
                        color="#4caf50"
                        gradient="linear-gradient(135deg, #4caf50, #388e3c)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatisticsCard
                        icon={TrendingUp}
                        label="Utilization"
                        value={Math.round(statistics.utilizationPercentage)}
                        color="#ff9800"
                        gradient="linear-gradient(135deg, #ff9800, #f57c00)"
                        suffix="%"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatisticsCard
                        icon={Kitchen}
                        label="Total Capacity"
                        value={(() => {
                          const config = pantryConfig[currentPantry];
                          const totalCapacity = statistics.totalCapacity;

                          if (config.unit === "g" && totalCapacity >= 1000) {
                            return `${Math.round(totalCapacity / 1000)}kg`;
                          } else if (
                            config.unit === "ml" &&
                            totalCapacity >= 1000
                          ) {
                            return `${Math.round(totalCapacity / 1000)}L`;
                          } else {
                            return `${Math.round(totalCapacity)}${config.unit}`;
                          }
                        })()}
                        color="#00bcd4"
                        gradient="linear-gradient(135deg, #00bcd4, #0097a7)"
                      />
                    </Grid>
                  </Grid>
                )}
              </Collapse>
            </Box>
          </motion.div>
        )}

        {/* Alerts Section */}
        {alerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Paper
              elevation={6}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 4,
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[900]}50 100%)`
                    : `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[50]}50 100%)`,
                border: `1px solid ${theme.palette.divider}30`,
                backdropFilter: "blur(20px)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#ff980020",
                      border: `2px solid ${theme.palette.warning.main}30`,
                    }}
                  >
                    <Notifications
                      sx={{ color: theme.palette.warning.main, fontSize: 24 }}
                    />
                  </Avatar>
                </motion.div>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Box Alerts
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {alerts.length} box(es) require attention
                  </Typography>
                </Box>
                <IconButton onClick={() => setAlertsExpanded(!alertsExpanded)}>
                  {alertsExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={alertsExpanded}>
                <Stack spacing={2}>
                  {alerts.map((box, index) => (
                    <motion.div
                      key={box.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Alert
                        severity={box.status.severity}
                        icon={<box.status.icon />}
                        action={
                          <Button
                            size="small"
                            onClick={() => handleIntiateUpdate(box)}
                            sx={{ fontWeight: 600 }}
                          >
                            Update
                          </Button>
                        }
                        sx={{
                          borderRadius: 3,
                          "& .MuiAlert-icon": {
                            fontSize: "1.5rem",
                          },
                        }}
                      >
                        <AlertTitle sx={{ fontWeight: 700 }}>
                          Box #{box.box_no} - {box.item_name || "Unlabeled"}
                        </AlertTitle>
                        <Typography variant="body2">
                          <strong>{Math.round(box.status.percentage)}%</strong>{" "}
                          filled ({box.box_filled}/{box.box_capacity}{" "}
                          {statistics?.unit}). {box.status.label}.
                        </Typography>
                      </Alert>
                    </motion.div>
                  ))}
                </Stack>
              </Collapse>
            </Paper>
          </motion.div>
        )}

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          {loading ? (
            <Grid container spacing={3}>
              {Array.from({ length: 12 }).map((_, index) => (
                <Grid
                  item
                  xs={12}
                  sm={viewMode === "grid" ? 6 : 12}
                  md={viewMode === "grid" ? 4 : 12}
                  lg={viewMode === "grid" ? 3 : 12}
                  key={index}
                >
                  <BoxSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : !data?.getBoxes?.length ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={4}
                sx={{
                  p: 6,
                  borderRadius: 4,
                  textAlign: "center",
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[900]})`
                      : `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
                  border: `2px dashed ${theme.palette.divider}`,
                }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                >
                  <IconComponent
                    sx={{
                      fontSize: 80,
                      color: config.color,
                      opacity: 0.5,
                      mb: 2,
                    }}
                  />
                </motion.div>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 700, mb: 2 }}
                >
                  No {config.title} Found
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  No boxes available for this pantry type
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ opacity: 0.7 }}
                >
                  Check back later or contact support if this is unexpected
                </Typography>
              </Paper>
            </motion.div>
          ) : (
            <Grid container spacing={3}>
              <AnimatePresence mode="wait">
                {data.getBoxes.map((box, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={viewMode === "grid" ? 6 : 12}
                    md={viewMode === "grid" ? 4 : 12}
                    lg={viewMode === "grid" ? 3 : 12}
                    key={box.id}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <InventoryBox
                        data={box}
                        onClick={() => handleIntiateUpdate(box)}
                      />
                    </motion.div>
                  </Grid>
                ))}
              </AnimatePresence>
            </Grid>
          )}
        </motion.div>

        {/* Enhanced Scroll to Top */}
        <AnimatePresence>
          {showScrollTop && (
            <motion.div
              initial={{ opacity: 0, scale: 0, rotate: -180 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0, rotate: 180 }}
              transition={{ duration: 0.3, type: "spring" }}
              style={{
                position: "fixed",
                bottom: 24,
                right: 24,
                zIndex: 1000,
              }}
            >
              <Tooltip title="Back to Top">
                <Fab
                  color="primary"
                  size="large"
                  onClick={scrollToTop}
                  sx={{
                    background: config.gradient,
                    boxShadow: `0 8px 32px ${config.color}40`,
                    width: 64,
                    height: 64,
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                    transition: "transform 0.2s ease",
                  }}
                >
                  <motion.div
                    animate={{ y: [-2, 2, -2] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <KeyboardArrowUp sx={{ fontSize: 32, color: "white" }} />
                  </motion.div>
                </Fab>
              </Tooltip>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>

      {/* Update Modal */}
      <CustomModal
        open={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
      >
        <InventoryEditBox
          refetch={refetch}
          setShowModal={setShowUpdateModal}
          selectedBox={selectedBox}
          kitchenId={kitchenDetail?.kitchenId}
          userId={userdata?.id}
        />
      </CustomModal>
    </>
  );
};

export default PantryBoxes;
